import { FLUSS_SCRIPT_TASK_DEFAULT_SCRIPT } from "modules/record/record-producer/components/record-producer-fields/code-editor/profiles";

const allowedKinds: Record<string, string[]> = {
    "bpmn:StartEvent": ["trigger", "signal", "message", "timer"],
    "bpmn:ServiceTask": ["service"],
    "bpmn:SendTask": ["service"],
    "bpmn:BusinessRuleTask": ["service"],
    "bpmn:UserTask": ["assignment", "approval", "task_form", "user_task_wait"],
    "bpmn:ManualTask": ["assignment", "task_form"],
    "bpmn:ScriptTask": ["script"],
};

export const removeIncompatibleFlussBindings = (shape: any) => {
    const extension = shape?.businessObject?.extensionElements;
    if (!extension?.values) return;
    const allowed = allowedKinds[shape.type] || [];
    extension.values = extension.values.filter((value: any) => {
        if (value.$type !== "fluss:Binding") return true;
        if (!allowed.includes(value.kind)) return false;
        if (shape.type === "bpmn:BusinessRuleTask" && value.kind === "service" && value.actionId) return false;
        return true;
    });
};

const intersects = (left: any, right: any) =>
    left.x < right.x + right.width && left.x + left.width > right.x &&
    left.y < right.y + right.height && left.y + left.height > right.y;

const isEscalationBoundary = (shape: any) =>
    shape?.type === "bpmn:BoundaryEvent" &&
    shape.businessObject?.eventDefinitions?.some((definition: any) => definition.$type === "bpmn:EscalationEventDefinition");

// bpmn-moddle omits the BPMN default (true) even if it was explicitly selected.
// The compiler keeps a legacy missing attribute non-interruptive, so preserve
// an explicit true in $attrs on execute, undo, redo and import.
const syncEscalationBoundaryAttributes = (elementRegistry: any) => {
    elementRegistry.filter(isEscalationBoundary).forEach((shape: any) => {
        const businessObject = shape.businessObject;
        businessObject.$attrs ||= {};
        if (Object.prototype.hasOwnProperty.call(businessObject, "cancelActivity") && businessObject.cancelActivity === true) {
            businessObject.$attrs.cancelActivity = "true";
        } else {
            delete businessObject.$attrs.cancelActivity;
        }
    });
};

const ensureScriptBinding = (shape: any, modeling: any, moddle: any) => {
    if (shape?.type !== "bpmn:ScriptTask") return;
    const businessObject = shape.businessObject;
    let extensionElements = businessObject.extensionElements;
    const existing = extensionElements?.values?.find((value: any) => value.$type === "fluss:Binding" && value.kind === "script");
    if (existing?.script?.body) return;
    if (!extensionElements) {
        extensionElements = moddle.create("bpmn:ExtensionElements", { values: [] });
        modeling.updateProperties(shape, { extensionElements });
    }
    const binding = existing || moddle.create("fluss:Binding", { kind: "script" });
    if (!existing) {
        binding.$parent = extensionElements;
        modeling.updateModdleProperties(shape, extensionElements, {
            values: [...(extensionElements.values || []), binding],
        });
    }
    modeling.updateModdleProperties(shape, binding, {
        script: moddle.create("fluss:Script", { body: FLUSS_SCRIPT_TASK_DEFAULT_SCRIPT }),
    });
};

export function FlussElementReplaceBehavior(this: any, eventBus: any, modeling: any, elementRegistry: any, moddle: any) {
    eventBus.on("commandStack.changed", () => syncEscalationBoundaryAttributes(elementRegistry));
    eventBus.on("import.done", () => syncEscalationBoundaryAttributes(elementRegistry));
    eventBus.on("replace.end", (event: any) => {
        if (isEscalationBoundary(event.newElement) && !isEscalationBoundary(event.element)) {
            modeling.updateProperties(event.newElement, { cancelActivity: false });
        }
    });
    eventBus.on("commandStack.shape.create.postExecute", (event: any) => {
        const shape = event.context?.shape;
        if (isEscalationBoundary(shape)) modeling.updateProperties(shape, { cancelActivity: false });
        ensureScriptBinding(shape, modeling, moddle);
    });
    eventBus.on("commandStack.shape.replace.postExecute", (event: any) => {
        const { newShape, oldShape } = event.context;
        removeIncompatibleFlussBindings(newShape);
        if (newShape?.type === "bpmn:ScriptTask" && oldShape?.type !== "bpmn:ScriptTask") {
            ensureScriptBinding(newShape, modeling, moddle);
        }
        if (newShape?.type !== "bpmn:SubProcess" || newShape.collapsed || oldShape?.type === "bpmn:SubProcess") return;

        const rootShapes = elementRegistry.filter((element: any) =>
            element.type !== "label" && !element.waypoints && element.parent === newShape.parent && element.id !== newShape.id);
        const incomingSources = (newShape.incoming || []).map((connection: any) => connection.source)
            .filter((shape: any) => shape?.parent === newShape.parent);
        const leftNeighbors = rootShapes.filter((shape: any) =>
            shape.x < oldShape.x && intersects(newShape, shape));
        const leftEdge = Math.max(...[...incomingSources, ...leftNeighbors]
            .map((shape: any) => shape.x + shape.width + 40), -Infinity);
        if (Number.isFinite(leftEdge) && newShape.x < leftEdge) {
            modeling.moveShape(newShape, { x: leftEdge - newShape.x, y: 0 });
        }

        const rightEdge = newShape.x + newShape.width + 40;
        const rightShapes = rootShapes.filter((shape: any) => shape.x >= oldShape.x && !shape.host);
        const overlappingRight = rightShapes.filter((shape: any) => intersects(newShape, shape));
        if (overlappingRight.length) {
            modeling.moveElements(rightShapes, {
                x: Math.max(...overlappingRight.map((shape: any) => rightEdge - shape.x)), y: 0,
            });
        }
        [...(newShape.incoming || []), ...(newShape.outgoing || [])].forEach((connection: any) =>
            modeling.layoutConnection(connection));
    });
}

(FlussElementReplaceBehavior as any).$inject = ["eventBus", "modeling", "elementRegistry", "moddle"];

export const flussElementReplaceModule = {
    __init__: ["flussElementReplaceBehavior"],
    flussElementReplaceBehavior: ["type", FlussElementReplaceBehavior],
};
